import { Context, Effect, Fiber, Scope } from "effect";

export interface AcceptedWorkShape {
  readonly join: <A, E, R>(operation: Effect.Effect<A, E, R>) => Effect.Effect<A, E, R>;
}
interface AcceptedOwner {
  readonly identity: object;
  readonly scope: Scope.Scope;
}
const AcceptedOperation = Context.Reference<AcceptedOwner | undefined>("workerstation/frontend/AcceptedOperation", { defaultValue: () => undefined });

/** Nested work joins the current accepted operation's scope. The outer action
 * owns every child; fresh actions still pass the root admission check. */
export const makeAcceptedWork: (root: Scope.Scope, admission?: Scope.Scope, options?: { readonly startImmediately?: boolean }) => Effect.Effect<AcceptedWorkShape> = Effect.fn("Frontend.AcceptedWork.make")(function* (root: Scope.Scope, admission: Scope.Scope = root, options: { readonly startImmediately?: boolean } = {}) {
  const identity = {};
  const join = Effect.fn("Frontend.acceptedWork")(function* <A, E, R>(operation: Effect.Effect<A, E, R>) {
    const inherited = yield* AcceptedOperation;
    if (inherited?.identity === identity) {
      if (inherited.scope.state._tag === "Closed") return yield* Effect.interrupt;
      const child = yield* Effect.forkIn(Effect.scoped(operation), inherited.scope, { ...options, uninterruptible: true });
      return yield* Fiber.join(child);
    }
    const child = yield* Effect.forkIn(Effect.suspend(() => admission.state._tag === "Closed" || root.state._tag === "Closed"
      ? Effect.interrupt : Effect.scoped(Effect.gen(function* () {
        const scope = yield* Scope.Scope;
        return yield* operation.pipe(Effect.provideService(AcceptedOperation, { identity, scope }));
      }))), root, { ...options, uninterruptible: true });
    return yield* Fiber.join(child);
  });
  return { join } satisfies AcceptedWorkShape;
});
